import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../store/authContext';
import { LockIcon } from '../utils/icons';
import ChangePasswordModal from './ChangePasswordModal';
import './Sidebar.css';

const NAV_ITEMS = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: GridIcon },
      { to: '/leads', label: 'Leads', icon: UsersIcon },
    ],
  },
  {
    label: 'Management',
    items: [
      { to: '/users', label: 'Team', icon: TeamIcon, roles: ['admin', 'super_admin'] },
      { to: '/settings', label: 'Settings', icon: SettingsIcon, roles: ['admin', 'super_admin'] },
    ],
  },
];

const SUPER_ADMIN_ITEMS = [
  {
    label: 'Super Admin',
    items: [
      { to: '/super-admin/tenants', label: 'Organisations', icon: BuildingIcon },
    ],
  },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const isOrgUser = user?.role && user.role !== 'super_admin';
  const groups = user?.role === 'super_admin'
    ? [
        { label: 'Overview', items: [{ to: '/dashboard', label: 'Dashboard', icon: GridIcon }] },
        ...SUPER_ADMIN_ITEMS,
      ]
    : NAV_ITEMS;

  return (
    <aside className="sidebar">
      {/* Logo */}
      <div className="sidebar-logo">
        <div className="sidebar-logo-mark">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M9 1L16 6V17H11V12H7V17H2V6L9 1Z" fill="currentColor" fillOpacity="0.9"/>
            <path d="M9 1L16 6V17H11V12H7V17H2V6L9 1Z" stroke="currentColor" strokeWidth="0.5" strokeOpacity="0.4"/>
          </svg>
        </div>
        <div className="sidebar-logo-text">
          <span className="sidebar-logo-brand">Realty</span>
          <span className="sidebar-logo-crm">CRM</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {groups.map((group) => {
          const visibleItems = group.items.filter(
            (item) => !item.roles || item.roles.includes(user?.role)
          );
          if (!visibleItems.length) return null;
          return (
            <div key={group.label} className="sidebar-group">
              <p className="sidebar-group-label section-label">{group.label}</p>
              {visibleItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'sidebar-link--active' : ''}`
                  }
                >
                  <item.icon size={15} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">
            {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="sidebar-user-info">
            <p className="sidebar-user-name">{user?.name || user?.email}</p>
            <p className="sidebar-user-role">{formatRole(user?.role)}</p>
          </div>
        </div>
        {isOrgUser && (
          <button
            className="sidebar-action btn-icon"
            onClick={() => setShowChangePassword(true)}
            title="Change password"
          >
            <LockIcon size={14} />
          </button>
        )}
        <button className="sidebar-logout btn-icon" onClick={logout} title="Sign out">
          <LogoutIcon size={14} />
        </button>
      </div>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </aside>
  );
}

function formatRole(role) {
  if (!role) return '';
  return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ─── SVG Icons ──────────────────────────────────────────────────────────── */

function GridIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="9" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="1" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="9" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
    </svg>
  );
}

function UsersIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M1 13.5C1 11.015 3.239 9 6 9s5 2.015 5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <circle cx="11.5" cy="5" r="2" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M13.5 13C13.5 11.343 12.657 9.9 11.5 9.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}

function TeamIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M3 13.5C3 11.015 5.239 9 8 9s5 2.015 5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function SettingsIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M2.929 2.929l1.414 1.414M11.657 11.657l1.414 1.414M2.929 13.071l1.414-1.414M11.657 4.343l1.414-1.414" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function BuildingIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <rect x="2" y="4" width="8" height="11" rx="1" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="10" y="7" width="4" height="8" rx="1" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5 7.5h2M5 10h2M5 12.5h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M12 10h0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function LogoutIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M10.5 11L14 8l-3.5-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}
